import { createApp, type App as VueApp } from 'vue'
import { createPinia } from 'pinia'
import { QueryClient, VueQueryPlugin } from '@tanstack/vue-query'

import AppRoot from './App.vue'
import { apiBaseUrl } from './lib/http/base-url.js'
import { createFetchClient } from './lib/http/fetch-client.js'
import { ApiError } from './lib/http/types.js'
import type { Interceptor } from './lib/http/client.js'
import { createAppRouter } from './router/index.js'
import { createAccountService } from './services/account/account.service.js'
import { createAnalyticsService } from './services/analytics/analytics.service.js'
import { createAppCatalogService } from './services/app-catalog/app-catalog.service.js'
import { createAuditService } from './services/audit/audit.service.js'
import { createShopEventService } from './services/shop-event/shop-event.service.js'
import { createAuthService } from './services/auth/auth.service.js'
import { createOperatorService } from './services/operator/operator.service.js'
import { createOverviewService } from './services/overview/overview.service.js'
import { createPlanService } from './services/plan/plan.service.js'
import { createPromotionService } from './services/promotion/promotion.service.js'
import { createReportService } from './services/report/report.service.js'
import { createRevenueService } from './services/revenue/revenue.service.js'
import { createRoleService } from './services/role/role.service.js'
import { createShopService } from './services/shop/shop.service.js'
import { createShopifyFunctionService } from './services/shopify-function/shopify-function.service.js'
import { createSubscriptionService } from './services/subscription/subscription.service.js'
import { createTranslationService } from './services/translation/translation.service.js'
import { createWidgetService } from './services/widget/widget.service.js'
import { useToast } from './composables/toast/use-toast.js'
import { reportError } from './utils/report-error.util.js'
import { useAuthStore } from './stores/auth/auth.store.js'
import { SERVICES, type ServiceContainer } from './types/shared/service.type.js'

/**
 * Composition. **The only place that knows how the pieces connect.**
 *
 * This is the one module allowed to construct an HTTP client, read the base URL or register
 * an interceptor — a boundary `eslint.config.mjs` enforces, so nothing else can reach for
 * `fetch` or quietly build a second client with different settings.
 *
 * Separate from `main.ts` so a test can build the whole application without mounting it.
 */
export function createApplication(): { app: VueApp<Element>; ready: Promise<void> } {
  const app = createApp(AppRoot)
  const pinia = createPinia()
  const router = createAppRouter()

  const http = createFetchClient({
    baseUrl: apiBaseUrl(),
    interceptors: [unauthenticatedRedirect(router)],
    /**
     * Refresh through the client itself, so the call carries the same base URL, timeout
     * and credential mode as everything else — a second code path here is a second place
     * for an absolute URL or a missing cookie to hide.
     *
     * `http` is referenced before it is assigned, which is safe because this runs only
     * when a request has already 401'd, long after construction.
     */
    refreshSession: async () => {
      try {
        await createAuthService(http).refresh()

        return true
      } catch {
        // An expired or revoked refresh token is the ordinary end of a session, not an
        // error to surface. The caller falls through to the login redirect.
        return false
      }
    },
  })

  const services: ServiceContainer = {
    auth: createAuthService(http),
    account: createAccountService(http),
    operator: createOperatorService(http),
    role: createRoleService(http),
    audit: createAuditService(http),
    shopEvent: createShopEventService(http),
    shop: createShopService(http),
    promotion: createPromotionService(http),
    appCatalog: createAppCatalogService(http),
    revenue: createRevenueService(http),
    analytics: createAnalyticsService(http),
    widget: createWidgetService(http),
    plan: createPlanService(http),
    subscription: createSubscriptionService(http),
    shopifyFunction: createShopifyFunctionService(http),
    overview: createOverviewService(http),
    translation: createTranslationService(http),
    report: createReportService(http),
  }

  // `pinia` before `provide`, and both before any store is used: a setup store resolves
  // `inject()` through the app's context, so a store constructed before the provide would
  // throw from `useServices()`.
  app.use(pinia)
  app.provide(SERVICES, services)
  app.use(VueQueryPlugin, { queryClient: createQueryClient() })
  app.use(router)
  installErrorReporting(app, router)

  /**
   * Hydrate before the first navigation resolves.
   *
   * The router's guard awaits `auth.ready` itself, so this is not load-bearing for
   * correctness — it is what stops the first paint being the login screen for an operator
   * who is signed in.
   */
  const ready = useAuthStore().hydrate()

  return { app, ready }
}

/**
 * What happens to an error nothing caught.
 *
 * - A render error is the `ErrorBoundary`'s. What reaches here is an event handler or a
 *   promise that threw: the screen is intact, the action did not happen, and the operator
 *   must be told — a sticky toast, with the message a developer will ask for.
 * - A navigation that cannot load its chunk is a deploy that replaced the files under a
 *   tab that was already open. One reload fixes it, and the guard against reloading
 *   forever is the session-scoped marker rather than a timer.
 */
function installErrorReporting(app: VueApp<Element>, router: ReturnType<typeof createAppRouter>): void {
  const toast = useToast()

  app.config.errorHandler = (error) => {
    reportError('unhandled', error)
    toast.error('Something went wrong', error instanceof Error ? error.message : String(error))
  }

  router.onError((error: unknown, to) => {
    const message = error instanceof Error ? error.message : String(error)
    const staleChunk = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(message)
    const marker = `zapio:reloaded:${to.fullPath}`

    if (staleChunk && sessionStorage.getItem(marker) === null) {
      sessionStorage.setItem(marker, '1')
      window.location.assign(to.fullPath)

      return
    }

    reportError('router', error)
    toast.error('Could not open that screen', message)
  })
}

/**
 * The query cache's defaults, set once.
 *
 * - **No retries.** The HTTP kernel already maps every failure to one `ApiError`; a retry
 *   that matters belongs where the idempotency is known (the service), not in a cache that
 *   would replay a POST. A 403 retried three times is three audit-log lines.
 * - **`staleTime: 30s`.** An operator tabbing between a list and a detail should not refetch
 *   the list every time; a write invalidates explicitly (§5), which is the mechanism that
 *   keeps the cache honest — not a timer.
 * - **No refetch on window focus.** The support team alt-tabs to Shopify constantly; a
 *   refetch on every return is load on the login path with no new information behind it.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: false, staleTime: 30_000, refetchOnWindowFocus: false },
      mutations: { retry: false },
    },
  })
}

/**
 * The one 401 handler — which **verifies before it acts**.
 *
 * ## Why it cannot simply trust a 401
 *
 * On this API a 401 does not only mean "your session is gone". Several admin routes read
 * a merchant's Shopify store, and the credential provider answers 401 when that
 * merchant's offline token is dead. Signing the operator out for it was a real defect:
 * opening a promotion's diagnostics for a shop with an expired token cleared the session,
 * pushed `/login`, and the guard — finding the cookie perfectly valid — bounced them to
 * the dashboard. The screen built to explain a broken merchant credential was the screen
 * a broken merchant credential closed.
 *
 * So a 401 now asks the server one question: is MY session still good? `hydrate()` is that
 * question. If it comes back with an operator, the 401 belonged to something downstream
 * and the panel stays where it is — the failing call renders its own error, which is the
 * component's job and not this interceptor's. Only a session that genuinely fails to
 * rehydrate sends the operator to sign in.
 *
 * The backend is fixed at its own end too; this is the half that protects every route
 * that has not been audited yet.
 *
 * ## The in-flight flag
 *
 * Six widgets can 401 at once on a dashboard. Six `router.push`es then cancel each other
 * and **none** completes — Vue Router aborts a navigation when a new one starts — leaving
 * the operator on an authenticated screen full of errors with no redirect. The flag makes
 * the first one win, and it also stops the `/auth/me` check below from re-entering this
 * handler when it is the call that 401s.
 */
/**
 * The session endpoints themselves. A 401 from one of these IS the answer — the store
 * reads it as "not signed in" — and re-checking from inside one would deadlock on the
 * very request whose failure triggered the check.
 */
const SESSION_PATHS = ['/admin/auth/me', '/admin/auth/refresh']

function unauthenticatedRedirect(router: ReturnType<typeof createAppRouter>): Interceptor {
  let settling = false

  return {
    onError: async (error: unknown, path: string) => {
      if (!(error instanceof ApiError) || !error.isUnauthenticated) return
      if (SESSION_PATHS.some((session) => path.includes(session))) return
      if (settling) return

      const current = router.currentRoute.value

      // The login screen's own 401 is the login attempt failing, and that belongs to the
      // form — redirecting would clear the error the operator needs to read.
      if (current.meta.public === true) return

      settling = true

      try {
        const auth = useAuthStore()

        // Re-reads `/auth/me` with the cookie. A 401 there is handled inside the store as
        // "not signed in" rather than thrown, so this never rejects.
        await auth.hydrate()

        // The session is fine: this 401 was about a merchant credential, not the operator.
        if (auth.isAuthenticated) return

        // `reset()` is deliberately NOT called — the hydrate above already cleared the
        // operator, and clearing `ready` as well would make the guard fetch `/auth/me` a
        // second time on the way to a screen that needs no session.
        await router.replace({ name: 'login', query: { redirect: current.fullPath } })
      } finally {
        settling = false
      }
    },
  }
}
